import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * Full-screen image lightbox.
 * - Click outside image or close button or press Esc to dismiss.
 * - Detects portrait vs landscape once the image loads and adapts its
 *   own aspect envelope (A4 portrait 1:1.414 → landscape 1.414:1).
 */
export default function Lightbox({ src, alt = "", open, onClose }) {
  const [orientation, setOrientation] = useState("portrait");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !src) return null;

  return createPortal(
    <div
      data-testid="lightbox"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6 lg:p-14 animate-in fade-in duration-200"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="lightbox-close"
        aria-label="Close"
        className="absolute top-6 right-6 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white transition-colors"
      >
        <X size={22} />
      </button>
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        onLoad={(e) => {
          const { naturalWidth, naturalHeight } = e.currentTarget;
          setOrientation(naturalWidth > naturalHeight ? "landscape" : "portrait");
        }}
        data-testid="lightbox-image"
        className={`max-w-full max-h-full object-contain shadow-2xl bg-white ${
          orientation === "landscape" ? "" : ""
        }`}
      />
    </div>,
    document.body
  );
}
